import "./DocumentationPage.css";

import { ArrowDownTree, ArrowUpTree, DeleteTree, EditTree, PlusSVG } from "../../../../lib/assets/icons";
import { ItemDefinition, Tree } from "../../../../lib/components/tree/Tree";
import { DropPosition } from "../../../../lib/components/tree/TreeItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, usePopup } from "../../../../lib/infrastructure/ui/UIServices";
import { useNavigate, useParams } from "react-router-dom";

import { AddPagePopup } from "./AddPagePopup";
import { AreaContainer } from "../../../../lib/layouts/containers/AreaContainer";
import { EditPagePopup } from "./EditPagePopup";
import { ErrorPopup } from "../../../../lib/components/popup/ErrorPopup";
import { GetPagesResponse } from "../../models/services/GetPagesResponse";
import { HorizontalResizer } from '../../../../lib/layouts/containers/horizontal-resizer/HorizontalResizer';
import { Loader } from "../../../../lib/components/loader/Loader";
import { PageChangedEvent } from "../../../../common/events/PagesChangedEvent";
import { PageContainer } from "../../../../lib/layouts/containers/page/PageContainer";
import { PageContent } from "./PageContent";
import { PageService } from "../../services/PageService";
import { PageTreeItem } from '../../models/PageTreeItem';
import { Page as ResponsePage } from "../../models/services/GetPagesResponse";
import { ScrollLayout } from '../../../../lib/layouts/containers/scroll-layout/ScrollLayout';
import { SuccessPopup } from "../../../../lib/components/popup/SuccessPopup";
import { WarningPopup } from "../../../../lib/components/popup/WarningPopup";
import { useServiceCallPro } from "../../../../common/services/UseServiceCall";
import { LoadError, LoadGate, PartialLocksBanner } from "../../../locks/components/LockStates";
import { LockBadge } from "../../../locks/components/LockBadge";
import { lockErrorKind } from "../../../locks/services/LockService";
import { useSpaceLocks } from "../../../locks/hooks/useSpaceLocks";

var pageSvc = new PageService();

function pageRoute(spaceId: string, pageId: string) {
  return `/spaces/${spaceId}/pages/${pageId}`;
}

function removePageTreeItemById(
  tree: PageTreeItem[],
  pageId: number
): number | null {

  const rootIndex = tree.findIndex(node => node.pageId === pageId);
  if (rootIndex !== -1) {
    tree.splice(rootIndex, 1); // Remove the item from the root array
    return null; // Root-level items don't have a parent, return null
  }

  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];

    // If the target node is found, remove it and return the parent ID
    if (node.children) {
      const childIndex = node.children.findIndex(child => child.pageId === pageId);

      if (childIndex !== -1) {
        node.children.splice(childIndex, 1);
        return node.pageId; // Return the parent's ID
      }

      // Recursively search in the children
      const result = removePageTreeItemById(node.children, pageId);
      if (result !== null) {
        return result;
      }
    }
  }

  return null; // Return null if not found
}

const mapGetPagesResponseToPages = (response: GetPagesResponse): PageTreeItem[] => {

  const mapToPageTreeItem = (page: ResponsePage): PageTreeItem => ({
    title: page.title,
    spaceId: page.spaceId,
    pageId: page.pageId,
    dateModified: page.dateModified,
    dateCreated: page.dateCreated,
    children: page.children?.map(mapToPageTreeItem)
  });

  return response.pages.map(mapToPageTreeItem);
}

function findInTree(tree: PageTreeItem[], id: number): PageTreeItem | null {
  for (const node of tree) {
    if (node.pageId === id) return node;
    if (node.children) {
      const found = findInTree(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

function isDescendant(tree: PageTreeItem[], ancestorId: number, candidateId: number): boolean {
  const ancestor = findInTree(tree, ancestorId);
  if (!ancestor?.children) return false;
  return treeContainsPageId(ancestor.children, candidateId);
}

function getSiblings(tree: PageTreeItem[], pageId: number): PageTreeItem[] | null {
  if (tree.findIndex(p => p.pageId === pageId) !== -1) return tree;
  for (const node of tree) {
    if (node.children) {
      if (node.children.findIndex(p => p.pageId === pageId) !== -1) return node.children;
      const found = getSiblings(node.children, pageId);
      if (found !== null) return found;
    }
  }
  return null;
}

function treeContainsPageId(tree: PageTreeItem[], id: number): boolean {
  for (const n of tree) {
    if (n.pageId === id) return true;
    if (n.children && treeContainsPageId(n.children, id)) return true;
  }
  return false;
}

function findParentInTree(tree: PageTreeItem[], id: number): PageTreeItem | null {
  for (const node of tree) {
    if (node.children?.some(c => c.pageId === id)) return node;
    if (node.children) {
      const found = findParentInTree(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

function optimisticReorder(tree: PageTreeItem[], draggedId: number, targetId: number, position: 'before' | 'after'): PageTreeItem[] {
  const cloned: PageTreeItem[] = structuredClone(tree);
  let draggedItem: PageTreeItem | null = null;

  const remove = (nodes: PageTreeItem[]): boolean => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].pageId === draggedId) { draggedItem = nodes.splice(i, 1)[0]; return true; }
      if (nodes[i].children && remove(nodes[i].children!)) return true;
    }
    return false;
  };

  const insert = (nodes: PageTreeItem[]): boolean => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].pageId === targetId) { nodes.splice(position === 'before' ? i : i + 1, 0, draggedItem!); return true; }
      if (nodes[i].children && insert(nodes[i].children!)) return true;
    }
    return false;
  };

  remove(cloned);
  if (draggedItem) insert(cloned);
  return cloned;
}

function optimisticReparent(tree: PageTreeItem[], draggedId: number, targetId: number): PageTreeItem[] {
  const cloned: PageTreeItem[] = structuredClone(tree);
  let draggedItem: PageTreeItem | null = null;

  const remove = (nodes: PageTreeItem[]): boolean => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].pageId === draggedId) { draggedItem = nodes.splice(i, 1)[0]; return true; }
      if (nodes[i].children && remove(nodes[i].children!)) return true;
    }
    return false;
  };

  const addToTarget = (nodes: PageTreeItem[]): boolean => {
    for (const node of nodes) {
      if (node.pageId === targetId) { node.children = [...(node.children ?? []), draggedItem!]; return true; }
      if (node.children && addToTarget(node.children)) return true;
    }
    return false;
  };

  remove(cloned);
  if (draggedItem) addToTarget(cloned);
  return cloned;
}

export function DocumentationPage() {
  const navigate = useNavigate();
  const openPopup = usePopup();
  const openEditor = useEditor();
  const { spaceId, pageId } = useParams();

  const [searchValue, setSearchValue] = useState<string>('');
  const [pages, setPages] = useState<PageTreeItem[]>([]);
  const [treeError, setTreeError] = useState<LoadError>();
  const spaceLocks = useSpaceLocks(spaceId, undefined, false);
  const reloadLocks = spaceLocks.reload;
  const lockMarks = spaceLocks.marks;

  const getPagesCall = useServiceCallPro(pageSvc.getPages);
  const movePageCall = useServiceCallPro(pageSvc.movePage);
  const deletePageCall = useServiceCallPro(pageSvc.deletePage);

  const leftAreaRef = useRef<HTMLDivElement>(null);
  const rightAreaRef = useRef<HTMLDivElement>(null);


  /****************************
  * DATA REQUESTS
  *****************************/

  const loadPages = useCallback((searchWord?: string) => {
    if (!spaceId) return;

    // the badges come from the manifest; a search only filters the tree, so it does not refetch them
    if (!searchWord) reloadLocks();
    getPagesCall.invoke(spaceId, searchWord)
      .then(response => {
        const currentPages = mapGetPagesResponseToPages(response)
        setTreeError(undefined);
        setPages(currentPages);
      })
      .catch(error => {
        // a space the caller cannot read is a 404 like a space that does not exist (OrbitalPlan/18 §3)
        const kind = lockErrorKind(error);
        setTreeError(kind === "not-found" || kind === "identity-unavailable" ? kind : undefined);
      });

  }, [setPages, spaceId, getPagesCall.invoke, reloadLocks]);

  /****************************
   * DATA MANIPULATION EFFECTS
   *****************************/

  useEffect(() => {
    loadPages(searchValue);
  }, [searchValue, spaceId]);


  useEffect(() => {
    // A page that is not in the tree is either hidden (locked) or gone: the page view's own load answers 404 and shows the
    // not-found view (or "Identity unavailable" while lock grants are unknown). Only a search result list jumps to its first hit.
    if (!spaceId || !pageId || !searchValue) return;

    const parsedPageId = parseInt(pageId, 10);

    const IsInsideTree = Number.isFinite(parsedPageId) && treeContainsPageId(pages, parsedPageId);

    if (!IsInsideTree) {
      const firstId = pages[0]?.pageId;
      if (firstId) {
        navigate(pageRoute(spaceId, String(firstId)), { replace: true });
      }
    }
  }, [pages]);

  const defaultPageId = useMemo(() => {
    return pages.length > 0 ? pages[0].pageId : undefined
  }, [pages]);

  const currentPageId = useMemo(() => {
    return pageId && parseInt(pageId);
  }, [pageId]);

  /****************************
   * USER ACTIONS
   *****************************/
  const handleSearchFilter = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleAddedPageCompleted = useCallback((newPageId: number) => {
    spaceId && navigate(pageRoute(spaceId, `${newPageId}`));
    loadPages();
  }, [spaceId,loadPages])

  const handleMovePage = useCallback(
    (space: number, page: number, instructionValue: number) => {
      movePageCall
        .invoke(space, page, { instruction: instructionValue })
        .then(() => {
          loadPages();
        });
    },
    [loadPages, movePageCall.invoke]
  );

  const handlePageReordered = useCallback((dragged: PageTreeItem, target: PageTreeItem, position: Exclude<DropPosition, 'inside'>) => {
    if (isDescendant(pages, dragged.pageId, target.pageId)) return;

    const targetParent = findParentInTree(pages, target.pageId);
    const targetLevelSiblings = (targetParent?.children ?? pages).filter(p => p.pageId !== dragged.pageId);
    const targetIdx = targetLevelSiblings.findIndex(p => p.pageId === target.pageId);

    if (targetIdx === -1) return;

    const previousPages = pages;
    setPages(optimisticReorder(pages, dragged.pageId, target.pageId, position));

    pageSvc.movePageTo(dragged.spaceId, dragged.pageId, {
      targetIndex: position === 'after' ? targetIdx + 1 : targetIdx,
      newParentPageId: targetParent?.pageId ?? null,
    }).catch((error) => {
      setPages(previousPages);
      loadPages();
      openPopup(<ErrorPopup>{error.response?.data?.message ?? error.message}</ErrorPopup>);
    });
  }, [pages, loadPages, openPopup]);

  const handlePageMoved = useCallback((dragged: PageTreeItem, target: PageTreeItem) => {
    if (isDescendant(pages, dragged.pageId, target.pageId)) return;

    const previousPages = pages;
    setPages(optimisticReparent(pages, dragged.pageId, target.pageId));

    pageSvc.movePageTo(dragged.spaceId, dragged.pageId, {
      targetIndex: target.children?.length ?? 0,
      newParentPageId: target.pageId,
    }).catch((error) => {
      setPages(previousPages);
      loadPages();
      openPopup(<ErrorPopup>{error.response?.data?.message ?? error.message}</ErrorPopup>);
    });
  }, [pages, loadPages, openPopup]);

  const handleDeleteOnConfirmClicked = useCallback(
    (space: number, page: number) => {
      deletePageCall
        .invoke(space, page)
        .then(() => {
          var pagesCopy = [...pages];
          var parentPage = removePageTreeItemById(pagesCopy, page);
          setPages(pagesCopy);

          spaceId && navigate(pageRoute(spaceId, `${parentPage || pagesCopy[0].pageId}`));

          openPopup(<SuccessPopup>Page deleted successfully!</SuccessPopup>);
          PageChangedEvent.firePageChanged();
        })
        .catch((error) => {
          if (error.response.data) {
            openPopup(<ErrorPopup>{error.response.data}</ErrorPopup>);
          } else {
            openPopup(<ErrorPopup>{error.message}</ErrorPopup>);
          }
        });
    }, [openPopup, deletePageCall.invoke, spaceId, pages]);

  const handleDeleteClicked = useCallback((v: PageTreeItem) => {
    openPopup(
      <WarningPopup onConfirmClicked={() => handleDeleteOnConfirmClicked(v.spaceId, v.pageId)}>
        Are you sure you want to delete this page?
      </WarningPopup>
    )
  }, [handleDeleteOnConfirmClicked, openPopup]);


  const handleAddClicked = useCallback((v?: PageTreeItem) => {
    if (!spaceId) return
    openPopup(
      <AddPagePopup
        onCompleted={handleAddedPageCompleted}
        spaceId={spaceId}
        pageId={v?.pageId.toString()}
      />
    )
  }, [handleAddedPageCompleted, openPopup, spaceId]);

  const handleEditClicked = useCallback((v: PageTreeItem) => {
    if (!spaceId) return

    openEditor(<EditPagePopup pageId={v.pageId.toString()} spaceId={spaceId} onSave={loadPages} />);
  }, [openEditor, spaceId, loadPages]);

  const handlePageClicked = useCallback((v: PageTreeItem) => {
    if (!v.pageId || !spaceId) return;
    navigate(pageRoute(spaceId, `${v.pageId}`));
  }, [spaceId, navigate]);

  /****************************
   * CSS & HTML
   *****************************/

  const itemDef = useMemo<ItemDefinition<PageTreeItem>>(
    () => ({
      render: (v) => <>{v.title}<LockBadge mark={lockMarks[v.pageId]} /></>,
      children: (v) => v.children,
      getId: (v) => v.pageId,
      onItemMoved: handlePageMoved,
      onItemReordered: handlePageReordered,
      canItemMoveTo: (dragged, target) => !isDescendant(pages, dragged.pageId, target.pageId),
      actions: (v) => {
        const siblings = getSiblings(pages, v.pageId) ?? [];
        const idx = siblings.findIndex(s => s.pageId === v.pageId);
        const canMoveUp = idx > 0;
        const canMoveDown = idx < siblings.length - 1;

        return [
          {
            label: "Add",
            icon: <PlusSVG />,
            onClick: handleAddClicked
          },
          ...(canMoveUp ? [{
            label: "Move Up",
            icon: <ArrowUpTree />,
            onClick: () => handleMovePage(v.spaceId, v.pageId, 1),
          }] : []),
          ...(canMoveDown ? [{
            label: "Move Down",
            icon: <ArrowDownTree />,
            onClick: () => handleMovePage(v.spaceId, v.pageId, 0),
          }] : []),
          {
            label: "Edit",
            icon: <EditTree />,
            onClick: handleEditClicked
          },
          {
            label: "Delete",
            icon: <DeleteTree />,
            onClick: handleDeleteClicked
          },
        ];
      },
      onItemClicked: handlePageClicked,
    }), [pages, lockMarks, handleMovePage, handlePageMoved, handlePageReordered, handleAddClicked, handleEditClicked, handleDeleteClicked, handlePageClicked]);


  if (getPagesCall.isLoading) {
    return (
      <PageContainer className="documentation-page">
        <AreaContainer>
          <Loader />
        </AreaContainer>
      </PageContainer>
    );
  }

  if (treeError) {
    // not-found → the app's not-found view; identity-unavailable → a retry state instead of an empty tree
    return (
      <PageContainer className="documentation-page">
        <AreaContainer>
          <LoadGate error={treeError} onRetry={() => loadPages(searchValue)}>{null}</LoadGate>
        </AreaContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="documentation-page page-no-scroll">
      <HorizontalResizer
        leftAreaRef={leftAreaRef}
        leftArea={
          <div className="documentation-page-tree-area" ref={leftAreaRef}>
            <PartialLocksBanner partial={spaceLocks.partial} />
            <ScrollLayout>
              <Tree
                isItemActive={(i) => i.pageId === currentPageId}
                items={pages}
                itemDefinition={itemDef}
                header={{
                  label: "Documentation",
                  actions: [
                    {
                      label: "Add",
                      icon: <PlusSVG />,
                      onClick: handleAddClicked
                    },
                  ],
                }}
                searchValue={searchValue}
                filter={handleSearchFilter}
              />
            </ScrollLayout>
          </div>
        }
        rightAreaRef={rightAreaRef}
        rightArea={
          <div ref={rightAreaRef} className="documentation-page-content-area">
            <PageContent defaultPage={defaultPageId} onSave={loadPages} lockMarks={lockMarks} />
          </div>
        } />
    </PageContainer>
  );
}
